import { useMemo } from "react";
import {
  CustomTypeTemplatePackage,
  CustomTypeTemplatePackageFilters,
  CustomTypeTemplatePackageFiltersTranslator,
} from "../../api/CustomTypes/CustomTypeTemplatePackages";
import { ColumnsSettings } from "../../common/tables/ColumnsSelector/ColumnsSelector";
import {
  GenericVirtualizedTableCells,
  SortState,
} from "../../common/tables/GenericVirtualizedTable/GenericVirtualizedTableTypes";
import {
  genericDefaultSortState,
  getAsyncSortingFn,
  UseEntityTableDefaultProps,
  UseEntityTableProps,
  useGenericVirtualizedTable,
  useGenericVirtualizedTableTabs,
} from "../../common/tables/GenericVirtualizedTable/useGenericVirtualizedTable";
import { TableTabsDict } from "../../common/tables/Tabs/TableTabsTypes";
import { LucideIcon } from "../../common/icon/LucideIcon";
import { NotSet } from "../../common/misc/UIconstants";
import { EntityTableProps } from "../../common/entity/EntityInterfaces";
import { useTabStore } from "../../common/tables/Tabs/useTabStore";
import TableView, { TableViewLabel } from "../../common/panels/TableView/TableView";
import { EntityTable } from "../../common/tables/EntityTable/EntityTable";
import { EntityFilterIndicator } from "../../common/tables/EntityFilterIndicator/EntityFilterIndicator";
import { SearchInput } from "../../common/forms/SearchInput/SearchInput";
import { EntityOrderBy, InferSortByOptions } from "../../api/GenericTypes";

export const switchCustomTypeTemplatePackagesDefaultSortState = (
  sortState: EntityOrderBy<InferSortByOptions<CustomTypeTemplatePackageFilters>>[] | null | undefined
): SortState<InferSortByOptions<CustomTypeTemplatePackageFilters>>[] => {
  const state = genericDefaultSortState(sortState);
  if (state) return state;
  return [
    {
      sortDirection: "ASC",
      headerId: "default-name",
      property: "NAME",
    },
  ];
};
const defaultFilter: CustomTypeTemplatePackageFilters = {
  sortBy: [{ property: "NAME", sortDirection: "ASC" }],
};

export const useCustomTypeTemplatePackagesTableDefaults = ({
  fieldLabels,
}: UseEntityTableDefaultProps<"customTypeTemplatePackages">) => {
  const defaults: ColumnsSettings<CustomTypeTemplatePackage> = useMemo(
    () => ({
      "default-id": { pos: 0, active: false, header: fieldLabels.id, property: "id" },
      "default-name": { pos: 1, active: true, header: fieldLabels.name, property: "name" },
      "default-description": { pos: 3, active: true, header: fieldLabels.description, property: "description" },
    }),
    [fieldLabels.description, fieldLabels.id, fieldLabels.name]
  );

  const tabStoreDefaults: TableTabsDict<CustomTypeTemplatePackage, CustomTypeTemplatePackageFilters, {}> = useMemo(
    () => ({
      default: {
        tabId: "default",
        type: "fixed",
        label: "All",
        title: "All",
        icon: "house",
        align: "left",
        xPos: 0,
        settings: {
          columnSettings: {},
          columnWidths: {},
          filters: defaultFilter,
          sidebarFilters: {},
        },
        forcedSettings: {
          columnSettings: {},
          columnWidths: {},
          filters: {},
          sidebarFilters: {},
        },
      },
    }),
    []
  );

  return { defaults, tabStoreDefaults };
};

export const useCustomTypeTemplatePackagesTableColumns = ({
  entityConstants,
  fieldLabels,
  sort,
  setSort,
}: UseEntityTableProps<"customTypeTemplatePackages">) => {
  const columns = useMemo(() => {
    let headers: GenericVirtualizedTableCells<CustomTypeTemplatePackage> = [
      {
        id: "default-id",
        Header: fieldLabels.id,
        accessor: (row) => <span style={{ color: "var(--gray-400)" }}>{row.id}</span>,
        width: 120,
        minWidth: 120,
        align: "center",
        ...getAsyncSortingFn("ID", sort, setSort),
      },
      {
        Header: fieldLabels.name,
        id: "default-name",
        accessor: (row) => (
          <div
            style={{
              overflow: "hidden",
              textOverflow: "ellipsis",
              fontWeight: 600,
              whiteSpace: "nowrap",
              marginRight: "5px",
            }}
          >
            <LucideIcon name={entityConstants.icon} color="var(--primary)" />
            <span> {row.name}</span>
          </div>
        ),
        minWidth: 150,
        width: 250,
        align: "left",
        ...getAsyncSortingFn("NAME", sort, setSort),
      },
      {
        Header: fieldLabels.description,
        id: "default-description",
        accessor: (row) => (
          <div
            style={{
              overflow: "hidden",
              textOverflow: "ellipsis",
              fontWeight: 600,
              whiteSpace: "nowrap",
              marginRight: "5px",
            }}
          >
            {row.description || NotSet}
          </div>
        ),
        minWidth: 150,
        width: 400,
        align: "left",
      },
    ];
    // Add custom field headers
    return headers;
  }, [entityConstants.icon, fieldLabels.description, fieldLabels.id, fieldLabels.name, setSort, sort]);
  return { columns };
};
export const CustomTypeTemplatePackagesTable = ({
  entityApi,
  entityConstants,
  fieldLabels,
  permissions,
  routes,
}: EntityTableProps<"customTypeTemplatePackages">) => {
  const { defaults, tabStoreDefaults } = useCustomTypeTemplatePackagesTableDefaults({ fieldLabels });

  const { resultsCount, onCountChange, onSelectionChange, onSelectionPermissions } =
    useGenericVirtualizedTable<CustomTypeTemplatePackage>();

  const { filters, forcedFilters, columnSetting, columnWidths, dispatchTabStore, tabsLoading, currentTab } =
    useTabStore<CustomTypeTemplatePackage, CustomTypeTemplatePackageFilters, {}>({
      resource: entityConstants.resource,
      defaults: tabStoreDefaults,
    });

  const { functionRef, sort, setSort, searchValue, setSearchValue } = useGenericVirtualizedTableTabs({
    currentTab,
    tabsLoading,
    filters,
    switchSortState: switchCustomTypeTemplatePackagesDefaultSortState,
    dispatchTabStore,
  });

  const { columns } = useCustomTypeTemplatePackagesTableColumns({ fieldLabels, entityConstants, sort, setSort });

  const memoizedFilters = useMemo(() => ({ ...filters, ...forcedFilters }), [filters, forcedFilters]);

  return (
    <TableView>
      <TableView.Head>
        <TableView.Head.Label>
          <TableViewLabel entityConstants={entityConstants} resultsCount={resultsCount} />
        </TableView.Head.Label>
      </TableView.Head>
      <TableView.Body>
        <TableView.Body.Content>
          <EntityTable>
            <EntityTable.Controls
              style={{
                borderTop: "0px",
                borderRadius: "0px",
              }}
            >
              <EntityFilterIndicator<CustomTypeTemplatePackage, CustomTypeTemplatePackageFilters>
                filters={filters}
                excludeFilters={{}}
                translatorConsts={CustomTypeTemplatePackageFiltersTranslator}
              />
              <SearchInput searchValue={searchValue} setSearchValue={setSearchValue} placeholder="Search" />
            </EntityTable.Controls>
            <EntityTable.Body<CustomTypeTemplatePackage, CustomTypeTemplatePackageFilters>
              functionRef={functionRef}
              entityConstants={entityConstants}
              filters={memoizedFilters}
              columns={columns}
              columnSelect
              columnSetting={columnSetting}
              columnWidths={columnWidths}
              defaultColumnSettings={defaults}
              dispatchTabStore={dispatchTabStore}
              setResultsCount={onCountChange}
              onSelectionChange={onSelectionChange}
              onSelectionPermissions={onSelectionPermissions}
              showPermissionColumn
              loading={tabsLoading}
            />
          </EntityTable>
        </TableView.Body.Content>
      </TableView.Body>
    </TableView>
  );
};
